import { useRef, useState } from 'react';

import type { RowData, Column as TanStackColumn } from '@tanstack/react-table';

import {
  ActionButton,
  Column,
  DateTimePicker,
  IconButton,
  Menu,
  NumberField,
  Row,
  Switch,
  TextField,
} from '@scaleaq/scaleui';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { CompactMultiSelect } from '@/components/compactMultiSelect';
import { Icon } from '@/utils';
import { fromApiDateTime, toApiDateTime } from '@/utils';

import type { SiteControlTableFeatures } from './tableFeatures.ts';

type FilterValue =
  | string
  | string[]
  | [string, string]
  | [number, number]
  | [number | null, number | null]
  | boolean
  | null;

type FilterColumn<T extends RowData> = TanStackColumn<SiteControlTableFeatures, T, unknown>;

function FilterContent<T extends RowData>({
  column,
  setValue,
  value,
}: {
  column: FilterColumn<T>;
  setValue: (valueOrUpdater: FilterValue | ((old: FilterValue) => FilterValue)) => void;
  value: FilterValue;
}) {
  const { t } = useTranslation(['common']);
  const { filterVariant, options } = column.columnDef.meta ?? {};
  switch (filterVariant) {
    case 'date':
      return (
        <Column>
          <Row>
            <DateTimePicker
              label={t('from')}
              date={fromApiDateTime(
                (value as [string, string])?.[0] || toApiDateTime(DateTime.now())
              )}
              onChange={value =>
                setValue(old => {
                  const prev = old as [string, string] | null;
                  return [value ? toApiDateTime(value) : '', prev?.[1] ?? ''];
                })
              }
            />
            <DateTimePicker
              label={t('to')}
              date={fromApiDateTime(
                (value as [string, string])?.[1] || toApiDateTime(DateTime.now())
              )}
              onChange={value =>
                setValue(old => {
                  const prev = old as [string, string] | null;
                  return [prev?.[0] ?? '', value ? toApiDateTime(value) : ''];
                })
              }
            />
          </Row>
        </Column>
      );
    case 'range':
      return (
        <Column>
          <Row>
            <NumberField
              label={t('from')}
              value={(value as [number, number])?.[0] ?? undefined}
              onChange={value =>
                setValue(old => {
                  const prev = old as [number, number] | [number | null, number | null] | null;
                  return [value ?? null, prev?.[1] ?? null];
                })
              }
              placeholder={t('min')}
            />
            <NumberField
              label={t('to')}
              value={(value as [number, number])?.[1] ?? undefined}
              onChange={value =>
                setValue(old => {
                  const prev = old as [number, number] | [number | null, number | null] | null;
                  return [prev?.[0] ?? null, value ?? null];
                })
              }
              placeholder={t('max')}
            />
          </Row>
        </Column>
      );
    case 'select':
      return (
        <Column>
          <CompactMultiSelect
            options={options || []}
            onChange={e => setValue(e)}
            value={value as string[]}
          />
        </Column>
      );
    case 'boolean':
      return (
        <Column>
          <Switch onChange={value => setValue(value)} value={String(value)} />
        </Column>
      );
    default:
      return (
        <TextField
          onChange={(value: string) => setValue(value)}
          placeholder={t('search')}
          type='text'
          value={(value ?? '') as string}
        />
      );
  }
}

export function Filter<T extends RowData>({ column }: { column: FilterColumn<T> }) {
  const { t } = useTranslation(['common']);
  const [filterOpen, setFilterOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [filterValue, setFilterValue] = useState<FilterValue>(
    column.getFilterValue() as FilterValue
  );
  /** Captured when the menu opens so Cancel restores the prior state instead of clearing. */
  const valueWhenOpenedRef = useRef<FilterValue>(null);

  const openMenu = () => {
    valueWhenOpenedRef.current = column.getFilterValue() as FilterValue;
    setFilterValue(valueWhenOpenedRef.current);
    setFilterOpen(true);
  };

  const actionButtons = [
    {
      label: t('confirm'),
      onClick: () => {
        column.setFilterValue(filterValue);
        setFilterOpen(false);
      },
    },
    {
      label: t('cancel'),
      onClick: () => {
        const restored = valueWhenOpenedRef.current;
        column.setFilterValue(restored);
        setFilterValue(restored);
        setFilterOpen(false);
      },
    },
  ] as [ActionButton, ActionButton];

  return (
    <Menu
      anchorEl={buttonRef as React.RefObject<HTMLButtonElement>}
      open={filterOpen}
      menuElement={<FilterContent column={column} setValue={setFilterValue} value={filterValue} />}
      onClose={() => setFilterOpen(false)}
      actionButtons={actionButtons}
    >
      <IconButton
        ref={buttonRef}
        size='large'
        Icon={<Icon.ListFilter size='large' />}
        onClick={openMenu}
      />
    </Menu>
  );
}
